<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { boolean, object, string } from 'yup';

const { tenant, tenantFaq } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    tenantFaq: {
        type: Object,
        required: false,
    },
});

const { route } = useRoute();

const form = reactive({
    hideItFromTheCustomer: tenantFaq ? tenantFaq.hideItFromTheCustomer : false,
    question: tenantFaq ? tenantFaq.question : '',
    answer: tenantFaq ? tenantFaq.answer : '',
});

const validationSchema = object({
    hideItFromTheCustomer: boolean().required('Ce champ est obligatoire.'),
    question: string().required('Ce champ est obligatoire.'),
    answer: string().required('Ce champ est obligatoire.'),
});

const isSubmitting = ref(false);

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.postFaq', {
                tenant: tenant.id,
                ...(tenantFaq ? { tenantFaq: tenantFaq.id } : {}),
            }),
            {
                hideItFromTheCustomer: form.hideItFromTheCustomer,
                question: form.question,
                answer: form.answer,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.faqs', {
                    tenant: tenant.id,
                }),
            );
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <FormContent>
                <SwitchField
                    v-model="form.hideItFromTheCustomer"
                    label="Cacher au client"
                    field-name="hideItFromTheCustomer"
                    field-id="hideItFromTheCustomer"
                />

                <InputField
                    v-model="form.question"
                    label="Question"
                    field-name="question"
                    field-id="question"
                    required
                />

                <EditorField
                    v-model="form.answer"
                    label="Réponse"
                    field-name="answer"
                    field-id="answer"
                    :media-path="'tenant/' + tenant.id + '/faq'"
                    required
                />
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="tenantFaq"
                    :url="
                        route('front.projectManagement.deleteFaq', {
                            tenant: tenant.id,
                            tenantFaq: tenantFaq.id,
                        })
                    "
                    :callback="
                        () => {
                            router.visit(
                                route('front.projectManagement.faqs', {
                                    tenant: tenant.id,
                                }),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
